import React, { useState, useEffect, useCallback, useRef } from 'react';
import './SearchSection.css';

export interface SearchSectionProps<T> {
  // Selection mode: 'multiple' uses selectedIds, 'single' uses selectedItem
  selectionMode: 'multiple' | 'single';
  
  // Multiple selection props
  selectedIds?: number[];
  onSelectionChange?: (ids: number[]) => void;
  
  // Single selection props
  selectedItem?: T | null;
  onItemSelect?: (item: T | null) => void;
  
  // Search configuration
  searchFunction: (searchTerm: string, additionalParams?: any) => Promise<T[]>;
  getByIdFunction?: (id: number) => Promise<T>;
  filterSelectedFromResults?: (results: T[], selectedIds: number[]) => T[];
  
  // Display configuration
  renderResultItem: (item: T, onAdd: () => void, isDisabled?: boolean) => React.ReactNode;
  renderSelectedItem: (item: T, onRemove: () => void) => React.ReactNode;
  getItemId: (item: T) => number;
  
  // UI props
  label?: string;
  placeholder?: string;
  maxSelections?: number;
  disabled?: boolean;
  emptyStateMessage?: string;
  selectedLabel?: string;
  searchResultsLabel?: string;
  loadingMessage?: string;
  
  // Additional search params (e.g., categoryTypeFilter for contacts)
  additionalSearchParams?: any;
  
  // Entity type for unique input IDs
  entityType: string;

  // Optional callbacks for parent components that need search activity state
  onSearchActivityChange?: (active: boolean) => void;
  onSearchFocusChange?: (focused: boolean) => void;
  onSearchTermChange?: (term: string) => void;
}

export function SearchSection<T>({
  selectionMode,
  selectedIds = [],
  onSelectionChange,
  selectedItem,
  onItemSelect,
  searchFunction,
  getByIdFunction,
  filterSelectedFromResults,
  renderResultItem,
  renderSelectedItem,
  getItemId,
  label = 'Search',
  placeholder = 'Search...',
  maxSelections,
  disabled = false,
  emptyStateMessage,
  selectedLabel,
  searchResultsLabel = 'Search Results',
  loadingMessage = 'Loading...',
  additionalSearchParams,
  entityType,
  onSearchActivityChange,
  onSearchFocusChange,
  onSearchTermChange
}: SearchSectionProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<T[]>([]);
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showInputForMultiple, setShowInputForMultiple] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Store getItemId in a ref to avoid recreating currentSelectedIds when function reference changes
  const getItemIdRef = useRef(getItemId);
  useEffect(() => {
    getItemIdRef.current = getItemId;
  }, [getItemId]);

  // Store searchFunction, filterSelectedFromResults, and additionalSearchParams in refs
  // to avoid recreating handleSearch when these props change
  const searchFunctionRef = useRef(searchFunction);
  const filterSelectedFromResultsRef = useRef(filterSelectedFromResults);
  const additionalSearchParamsRef = useRef(additionalSearchParams);
  
  useEffect(() => {
    searchFunctionRef.current = searchFunction;
  }, [searchFunction]);
  
  useEffect(() => {
    filterSelectedFromResultsRef.current = filterSelectedFromResults;
  }, [filterSelectedFromResults]);
  
  useEffect(() => {
    additionalSearchParamsRef.current = additionalSearchParams;
  }, [additionalSearchParams]);

  // Extract ID from selectedItem to avoid infinite loops when object reference changes
  // Store the ID in state so it only updates when the ID value actually changes
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const prevSelectedItemIdRef = useRef<number | null>(null);
  
  useEffect(() => {
    if (selectionMode === 'single' && selectedItem) {
      try {
        const id = getItemIdRef.current(selectedItem);
        // Only update if the ID actually changed
        if (id !== prevSelectedItemIdRef.current) {
          prevSelectedItemIdRef.current = id;
          setSelectedItemId(id != null ? id : null);
        }
      } catch (error) {
        console.error(`Error getting item ID for ${entityType}:`, error);
        if (prevSelectedItemIdRef.current !== null) {
          prevSelectedItemIdRef.current = null;
          setSelectedItemId(null);
        }
      }
    } else {
      if (prevSelectedItemIdRef.current !== null) {
        prevSelectedItemIdRef.current = null;
        setSelectedItemId(null);
      }
    }
  }, [selectionMode, selectedItem, entityType]);

  // Store currentSelectedIds in a ref to avoid recreating handleSearch when array reference changes
  const currentSelectedIdsRef = useRef<number[]>([]);
  const currentSelectedIds = React.useMemo(() => {
    if (selectionMode === 'multiple') {
      const ids = selectedIds || [];
      // Only update ref if the array contents actually changed
      const idsChanged = ids.length !== currentSelectedIdsRef.current.length || 
        ids.some((id, index) => id !== currentSelectedIdsRef.current[index]);
      if (idsChanged) {
        currentSelectedIdsRef.current = ids;
      }
      return currentSelectedIdsRef.current;
    } else {
      const id = selectedItemId != null ? [selectedItemId] : [];
      // Only update ref if the ID actually changed
      const idChanged = id.length !== currentSelectedIdsRef.current.length ||
        (id.length > 0 && id[0] !== currentSelectedIdsRef.current[0]);
      if (idChanged) {
        currentSelectedIdsRef.current = id;
      }
      return currentSelectedIdsRef.current;
    }
  }, [selectionMode, selectedIds, selectedItemId]);

  // Fetch selected items details when selection changes
  // Split into separate effects for single vs multiple to avoid infinite loops
  
  // Single selection mode - depend on selectedItemId, not selectedItem object reference
  useEffect(() => {
    if (selectionMode === 'single') {
      setSelectedItems(selectedItem ? [selectedItem] : []);
    }
    // Only re-run when selectedItemId changes, not when selectedItem object reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode, selectedItemId]);

  // Multiple selection mode - fetch items by ID
  useEffect(() => {
    if (selectionMode === 'multiple') {
      if (!getByIdFunction || !selectedIds || selectedIds.length === 0) {
        setSelectedItems([]);
        return;
      }

      setLoading(true);
      const fetchItems = async () => {
        try {
          const itemPromises = selectedIds.map(id => getByIdFunction(id));
          const items = await Promise.all(itemPromises);
          setSelectedItems(items);
        } catch (error) {
          console.error(`Error fetching selected ${entityType}s:`, error);
          setSelectedItems([]);
        } finally {
          setLoading(false);
        }
      };
      fetchItems();
    }
  }, [selectionMode, selectedIds, getByIdFunction, entityType]);

  // Search function - use refs for dependencies to avoid recreating on every render
  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const results = await searchFunctionRef.current(searchTerm.trim(), additionalSearchParamsRef.current);
      
      // Filter out already selected items - use ref to get latest value without causing recreation
      let filteredResults = results;
      if (filterSelectedFromResultsRef.current) {
        filteredResults = filterSelectedFromResultsRef.current(results, currentSelectedIdsRef.current);
      } else {
        filteredResults = results.filter(item => 
          !currentSelectedIdsRef.current.includes(getItemIdRef.current(item))
        );
      }
      
      // Limit to 5 results for display
      setSearchResults(filteredResults.slice(0, 5));
    } catch (error) {
      console.error(`Error searching ${entityType}s:`, error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [searchTerm, entityType]);

  // Add item to selection
  const addItem = useCallback((item: T) => {
    if (selectionMode === 'multiple') {
      if (maxSelections && (selectedIds?.length || 0) >= maxSelections) {
        return;
      }
      const newIds = [...(selectedIds || []), getItemIdRef.current(item)];
      onSelectionChange?.(newIds);
    } else {
      onItemSelect?.(item);
    }
    
    // Remove from search results
    setSearchResults(prev => prev.filter(i => getItemIdRef.current(i) !== getItemIdRef.current(item)));
    
    // Clear search
    setSearchTerm('');
  }, [selectionMode, selectedIds, maxSelections, onSelectionChange, onItemSelect]);

  // Remove item from selection
  const removeItem = useCallback((itemId: number) => {
    if (selectionMode === 'multiple') {
      const newIds = (selectedIds || []).filter(id => id !== itemId);
      onSelectionChange?.(newIds);
    } else {
      onItemSelect?.(null);
    }
  }, [selectionMode, selectedIds, onSelectionChange, onItemSelect]);

  // Handle search input change with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, handleSearch]);

  const inputId = `${entityType}Search`;
  const isMaxSelectionsReached = React.useMemo(() => 
    maxSelections ? (selectedIds?.length || 0) >= maxSelections : false,
    [maxSelections, selectedIds]
  );

  // Determine if search input should be visible
  // Show if: nothing selected OR single selection mode (when nothing selected)
  // Hide if: single selection mode AND one item is selected
  // For multiple: hide when items are selected unless showInputForMultiple is true
  const shouldShowSearchInput = React.useMemo(() => {
    if (selectionMode === 'single') {
      return selectedItems.length === 0;
    }
    if (isMaxSelectionsReached) {
      return false;
    }
    // For multiple selection: show if no items selected OR showInputForMultiple is true
    return selectedItems.length === 0 || showInputForMultiple;
  }, [selectionMode, selectedItems.length, showInputForMultiple, isMaxSelectionsReached]);

  // Handle "+" button click for multiple selection mode
  const handleAddButtonClick = useCallback(() => {
    setShowInputForMultiple(true);
    // Focus input after state update
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  // Handle Escape key to clear input and hide in multiple mode
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setSearchTerm('');
      setSearchResults([]);
      if (selectionMode === 'multiple') {
        setShowInputForMultiple(false);
      }
    }
  }, [selectionMode]);

  // Reset showInputForMultiple when an item is added (track previous length)
  const prevSelectedItemsLengthRef = useRef<number>(0);
  
  useEffect(() => {
    if (selectionMode === 'multiple' && showInputForMultiple) {
      // Check if an item was just added (length increased)
      if (selectedItems.length > prevSelectedItemsLengthRef.current) {
        // When an item is added, hide the input again
        setShowInputForMultiple(false);
        setSearchTerm('');
        setSearchResults([]);
      }
    }
    // Always update the ref to track current length
    prevSelectedItemsLengthRef.current = selectedItems.length;
  }, [selectionMode, selectedItems.length, showInputForMultiple]);

  useEffect(() => {
    if (isMaxSelectionsReached) {
      setShowInputForMultiple(false);
      setSearchTerm('');
      setSearchResults([]);
    }
  }, [isMaxSelectionsReached]);

  const isSearchActive = isInputFocused
    || searchTerm.trim() !== ''
    || searchResults.length > 0
    || searchLoading;

  useEffect(() => {
    onSearchActivityChange?.(isSearchActive);
  }, [isSearchActive, onSearchActivityChange]);

  useEffect(() => {
    onSearchTermChange?.(searchTerm);
  }, [searchTerm, onSearchTermChange]);

  const handleSearchInputFocus = useCallback(() => {
    setIsInputFocused(true);
    onSearchFocusChange?.(true);
  }, [onSearchFocusChange]);

  const handleSearchInputBlur = useCallback(() => {
    setIsInputFocused(false);
    onSearchFocusChange?.(false);
  }, [onSearchFocusChange]);

  return (
    <div className="search-section">
      {/* Label and search input (hidden in single mode once an item is selected) */}
      {(selectionMode !== 'single' || selectedItems.length === 0 || shouldShowSearchInput) && (
      <div className="app-form-group">
        {!(selectionMode === 'single' && selectedItems.length > 0) && (
          <div className="search-label-container">
            <label htmlFor={inputId} className="app-form-label">
              {label} {selectionMode === 'multiple' && selectedItems.length > 0 && `(${selectedItems.length})`}
            </label>
            {selectionMode === 'multiple' && selectedItems.length > 0 && !showInputForMultiple && (
              <button
                type="button"
                className="app-btn app-btn-primary app-btn-small"
                onClick={handleAddButtonClick}
                title="Add another item"
                aria-label="Add another item"
                disabled={disabled || isMaxSelectionsReached}
              >
                +
              </button>
            )}
          </div>
        )}
        
        {/* For single selection: Input field first (when nothing selected) */}
        {selectionMode === 'single' && shouldShowSearchInput && (
          <div className="search-input-group">
            <input
              id={inputId}
              ref={inputRef}
              type="text"
              className="app-form-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={handleSearchInputFocus}
              onBlur={handleSearchInputBlur}
              placeholder={placeholder}
              disabled={disabled || loading}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              name={`${entityType}-search-field`}
            />
            {searchLoading && <div className="search-loading">Searching...</div>}
          </div>
        )}
      </div>
      )}

      {/* Selected Items */}
      {selectedItems.length > 0 && (
        <div className="app-form-group selected-items-container">
          <div>
            {selectedItems.map((item) => (
              <div key={getItemIdRef.current(item)}>
                {renderSelectedItem(item, () => removeItem(getItemIdRef.current(item)))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* For multiple selection: Search input after selected items */}
      {selectionMode === 'multiple' && shouldShowSearchInput && (
        <div className="app-form-group">
          <div className="search-input-group">
            <input
              id={inputId}
              ref={inputRef}
              type="text"
              className="app-form-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={handleSearchInputFocus}
              onBlur={handleSearchInputBlur}
              placeholder={placeholder}
              disabled={disabled || loading}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              name={`${entityType}-search-field`}
            />
            {searchLoading && <div className="search-loading">Searching...</div>}
          </div>
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="app-form-group search-results-container">
          <h4 className="search-results-title">{searchResultsLabel}</h4>
          <div className="search-results-list">
            {searchResults.map((item) => (
              <div key={getItemIdRef.current(item)}>
                {renderResultItem(item, () => addItem(item), isMaxSelectionsReached)}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="app-form-group search-loading-state">
          <p>{loadingMessage}</p>
        </div>
      )}
    </div>
  );
}

